import { Link } from 'react-router-dom'
import KebabMenu from '@components/common/KebabMenu'
import { deriveState } from '@config/queueConfig'
import { DuplicateIcon, InfoIcon, SlidersIcon, TrashIcon } from './queueIcons'
import { canOpenRunResult } from './jobVisibility'

/**
 * Row actions: exactly ONE visible contextual primary plus a kebab for the rest.
 * (Design review: "one primary, and the rest go into a kebab menu.")
 *
 * The primary is chosen by state — the single thing a user most likely wants:
 *   running → Watch · ready → View report · failed → Fix & retry · expired → Run again
 */
const JobRowActions = ({ job, onOpen, onDismiss, onRerun }) => {
  // Resolve the state ONCE. Re-deriving via isResultAvailable(job) would read
  // job.state and could disagree with the effectiveState the row is rendered
  // from — which is exactly how an expired job gets a live "View report" link.
  // `effectiveState` is precomputed upstream by useMyReports; deriveState's own
  // Date.now() default only covers the case where it isn't.
  const state = job.effectiveState || deriveState(job)
  /**
   * CC-50231 TC4b — ownership gates every affordance on this row.
   *
   * Under firm/group scope this table lists OTHER people's runs, and this component
   * had no ownership check at all: a colleague's ready run got a live "View report"
   * link, a "Run again", and a "Dismiss from queue".
   *
   * The server was already correct — `getJob` is owner-scoped in SQL, so all three
   * would have been refused (verified live: 404 on the status, result and download
   * routes for a foreign job, with the owner getting 200 on the same id). So this is
   * **not** a data leak, and this change is not what stops one. What it fixes is a UI
   * that offers actions the server will refuse, and tells the reader a colleague's run
   * is theirs. `Dismiss` was the most misleading: the DELETE is owner-scoped too, so it
   * silently removed the row on screen and the next 5-second poll put it back.
   *
   * `!== false`, never truthiness: absent means "mine" (own-scope listings omit it).
   * Same shape as the schedules table, which already solved this for schedules.
   */
  const isOwner = job.isOwner !== false
  // Spec 169 — a CB administrator may also OPEN another user's private-schedule run
  // (`adminAccess`, cb-api's answer for this reader). Dismiss stays owner-only below.
  const canOpen = canOpenRunResult(job)
  const canView = canOpen && state === 'ready' && job.reportId
  const reportHref = canOpen && job.reportId ? `/reports/${job.reportId}` : null

  const items = [
    {
      // Clicking the row opens the drawer, but that is undiscoverable — the
      // kebab is where people look for "what can I do with this run?", so the
      // same destination has to be listed here too.
      key: 'details',
      label: 'View details',
      icon: <InfoIcon />,
      onSelect: () => onOpen?.(job),
    },
    // CC-53051 — these two items had IDENTICAL behaviour: both called `onRerun(job)`,
    // which navigated to the same bare `?jobId=` URL. So "Open report & edit filters"
    // and "Run again, same filters" did the same thing, and that thing was neither of
    // them — it opened the report and auto-ran with the user's PERSISTED filters.
    // The intent is now passed explicitly.
    ...(reportHref ? [{
      key: 'filters',
      label: state === 'failed' ? 'Change filters & retry' : 'Open report & edit filters',
      icon: <SlidersIcon />,
      // 'edit' → open with the job's filters pre-filled and WAIT. The user asked to
      // change something; running first would waste a run and could overwrite the
      // result they wanted to compare against.
      onSelect: () => onRerun?.(job, 'edit'),
    }] : []),
    ...(reportHref ? [{
      key: 'again',
      label: 'Run again, same filters',
      icon: <DuplicateIcon />,
      // 'rerun' → a real new run, with THIS JOB's filters. What the label always said.
      onSelect: () => onRerun?.(job, 'rerun'),
    }] : []),
    {
      key: 'copy',
      label: 'Copy job ID',
      icon: <DuplicateIcon />,
      onSelect: () => { navigator.clipboard?.writeText(job.id).catch(() => {}) },
    },
    ...(isOwner ? [{
      // NOT "Cancel": DELETE hard-deletes the row without stopping the in-flight
      // work or releasing the legacy process. Calling it Cancel would promise
      // something the backend does not do (spec 056 §Open — real cancel is Vlad's).
      //
      // Owner-only: the DELETE is owner-scoped server-side, so offering it on a
      // colleague's row promised a destructive action that could not happen — and,
      // because the client removed the row optimistically, appeared to have happened
      // until the next poll restored it.
      key: 'dismiss',
      label: 'Dismiss from queue',
      icon: <TrashIcon />,
      danger: true,
      dividerBefore: true,
      onSelect: () => onDismiss?.(job),
    }] : []),
  ]

  let primary = null
  if (!canOpen) {
    // Say WHY there is no action, rather than rendering an empty cell. A missing
    // affordance is indistinguishable from a regression — the same reasoning that put
    // the `downloadNote` in the drawer (CC-50233), and the same "Shared with you" chip
    // vocabulary the schedules table already uses.
    primary = (
      <span
        className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-semibold text-gray-600"
        title="A teammate's run. Its saved result was produced under their access, so only they can open it."
      >
        Theirs — view only
      </span>
    )
  } else if (canView) {
    primary = (
      /*
       * A2 (spec 118) — this was the ONLY solid-blue row action in the queue table,
       * `bg-cb-blue` with white text, while its three state-siblings just below and
       * the Schedules table's "Run now" were all the outlined `.btn-cb-toolbar`.
       *
       * Design review (Himaad): "I wouldn't make every row button compete as a
       * primary action on the page ... in these dense tables the best practice is
       * usually to let the data dominate." Jordan's landing point was to match the
       * mock's white button and, if it read too weak, "add a little bit of depth to
       * the border" rather than repaint it.
       *
       * So: the SAME outlined control the other three states already wear — white
       * fill — plus `shadow-xs`, which is exactly the mock's
       * `0 1px 2px rgba(0,0,0,.05)`.
       *
       * ⚠️ THE BORDER IS OVERRIDDEN AT THE CALL SITE, and that is the whole reason
       * `border-rc-seam!` is here. `.btn-cb-toolbar` ships
       * `border: 1px solid var(--color-rc-control)`, and #8b95a5 does NOT clear
       * SC 1.4.11 on every ground this button reaches. Measured, not asserted:
       *
       *   --color-rc-control #8b95a5   3.026:1 on #fff   2.896:1 on #f9fafb  FAIL
       *   --color-rc-seam    #6b7280   4.834:1           4.626:1             PASS
       *   --color-cb-blue    #064891   8.961:1           8.575:1   (what it WAS)
       *   the mock's border  #e5e7eb   1.238:1           1.185:1             FAIL
       *
       * Every DataGrid row carries `hover:bg-gray-50` (common/DataGrid.jsx:1571),
       * and rows here are click-to-open (QueueTable.jsx `onRowClick`), so #f9fafb is
       * not an edge case — it is the state immediately preceding every click. The
       * button keeps its own white fill and RESTING border there, because
       * `.btn-cb-toolbar:hover` has not fired; on a white button whose fill matches
       * the row, that 1px border is the only non-text thing identifying the control.
       * src/index.css:701 records #8b95a5 as FAIL on exactly that ground, and
       * index.css:439 already made this same substitution for this same reason on
       * `.btn-cb-neutral.btn-cb-split-r` (CC-53362). So #6b7280 introduces no new
       * hex: it is the colour `.btn-cb-toolbar:hover` already sets.
       *
       * WHY THE `!`, precisely — the general "utilities are inert beside an
       * unlayered class" rule is TRUE ONLY FOR A NORMAL DECLARATION. A bare
       * `border-rc-seam` compiles to `border-color: var(--color-rc-seam)` and loses
       * to the class's unlayered `border` shorthand. Tailwind v4's important
       * modifier compiles to `border-color: var(--color-rc-seam) !important`, and
       * per CSS Cascade 5 importance is sorted BEFORE layer origin — so it wins.
       * Verified by compiling this repo's own src/index.css with its own
       * tailwindcss 4.2.1, not reasoned about; both forms are pinned in
       * __tests__/contrastClaims.test.js. An earlier version of this comment said
       * the colour was "not solvable here" and owed entirely to the grid-chrome
       * stream. That was wrong, and it is the claim this override retires.
       *
       * The cost, stated: the resting border now equals what
       * `.btn-cb-toolbar:hover` sets, so pointer-hover feedback on the button
       * narrows to its fill (#fff -> #f9fafb) and its label (ink-2 -> ink). Hover
       * feedback is not required by any success criterion; `:focus-visible` is
       * untouched. `shadow-xs` was never contrast: 5% black over #f9fafb
       * composites to #edeeee, 1.112:1, and only below the bottom edge.
       *
       * STILL OWED, narrowed: the 29 OTHER `.btn-cb-toolbar` className call sites
       * (39 across 17 files, measured — 7 of them in this stream) keep #8b95a5 and
       * still fail on a hovered row. That token is src/index.css's to repaint and
       * this stream must not touch that file. See spec 118 §A2.
       *
       * ⚠️ `.btn-cb-toolbar` lives in the UNLAYERED src/index.css and therefore
       * DEFEATS any NON-IMPORTANT h-/text-/px-/py-/rounded-/border- utility written
       * beside it, at any specificity. `rounded-lg px-3 py-1.5 text-xs font-medium
       * text-white` and the two `bg-`/`border-` utilities were deleted in the same
       * edit rather than left to render nothing. `shadow-xs` is safe without a `!`:
       * the class sets no box-shadow, so there is nothing to defeat.
       *
       * Focus: `.btn-cb-toolbar:focus-visible` carries the app's 2px #1d4ed8 ring at
       * 1px offset, and this is an <a href> — so it stays in the tab order and shows
       * a visible indicator without any per-call-site styling (SC 2.1.1 / 2.4.7).
       */
      <Link
        to={`${reportHref}?jobId=${job.id}`}
        onClick={(e) => e.stopPropagation()}
        className="btn-cb-toolbar border-rc-seam! shadow-xs"
      >
        View report
      </Link>
    )
  } else if (state === 'running' || state === 'queued') {
    primary = (
      // `border-rc-seam!` on every one of these four for the reason spelled out at
      // "View report" above: the class's own #8b95a5 border is 2.896:1 on the row's
      // own hover tint and fails SC 1.4.11 there. The `!` is load-bearing.
      <button type="button" onClick={(e) => { e.stopPropagation(); onOpen?.(job) }} className="btn-cb-toolbar border-rc-seam! shadow-xs">
        Watch
      </button>
    )
  } else if (state === 'failed') {
    primary = (
      // "Fix & retry" means FIX first — open with the job's filters and wait. Re-running
      // a failed job unchanged would usually just fail again.
      <button type="button" onClick={(e) => { e.stopPropagation(); onRerun?.(job, 'edit') }} className="btn-cb-toolbar border-rc-seam! shadow-xs">
        Fix &amp; retry
      </button>
    )
  } else if (state === 'expired') {
    primary = (
      // The result is gone but the filters are not: re-run exactly what expired.
      <button type="button" onClick={(e) => { e.stopPropagation(); onRerun?.(job, 'rerun') }} className="btn-cb-toolbar border-rc-seam! shadow-xs">
        Run again
      </button>
    )
  }

  return (
    <div className="flex items-center justify-between gap-1.5">
      {primary}
      <KebabMenu items={items} label={`Actions for ${job.displayName || 'this job'}`} />
    </div>
  )
}

export default JobRowActions
